import React from 'react';
import { Flex, Text, View, IconButton, Icon, Icons } from '@smarterservices/smarter-ui';
import { BlockComponentProps, CalloutBlock as CalloutBlockType } from '../../types';
import BlockRenderer from '../../BlockRenderer';

// Define type-safe config mapping
type CalloutTypeConfig = {
  [key: string]: {
    background: string;
    borderColor: 'info' | 'success' | 'warning' | 'danger' | 'secondary';
    iconName: string;
    iconColor: 'success' | 'danger' | 'secondary' | 'primary';
    textColor: 'info' | 'success' | 'warning' | 'danger' | 'secondary';
  }
};

/**
 * CalloutBlock component renders a callout with an icon, title, and content
 * 
 * @param {BlockComponentProps<CalloutBlockType>} props - Component props
 * @returns {React.ReactElement} Rendered callout
 */
export const CalloutBlock: React.FC<BlockComponentProps<CalloutBlockType>> = ({ block }) => {
  // Default to info variant if not specified
  const variant = block.variant || 'info';

  // Map callout types to colors and icons
  const typeConfig: CalloutTypeConfig = {
    info: {
      background: 'primary',
      borderColor: 'info',
      iconName: 'info',
      iconColor: 'primary',
      textColor: 'info'
    },
    success: {
      background: 'primary',
      borderColor: 'success',
      iconName: 'check',
      iconColor: 'success',
      textColor: 'success'
    },
    warning: {
      background: 'primary',
      borderColor: 'warning',
      iconName: 'warning',
      iconColor: 'danger',
      textColor: 'warning'
    },
    note: {
      background: 'primary',
      borderColor: 'secondary',
      iconName: 'note',
      iconColor: 'secondary',
      textColor: 'secondary'
    }
  };

  const config = typeConfig?.[variant] || typeConfig['note'];

  return (
    <View
      as="div"
      background={config.background}
      padding="small"
      margin={block.margin || "small 0"}
    >
      <Flex direction="row" gap="xx-small" alignItems="start">
        {(block.icon && block.icon.name && Icons[block.icon.name]) && (
          <Flex.Item>
            <IconButton
              screenReaderLabel={`${variant} callout`}
              withBackground={false}
              withBorder={false}
              color={config.iconColor || "primary"}
              interaction="readonly"
              cursor='default'
              size="small"
              themeOverride={{
                dangerGhostColor: '#EE6D15',
              }}
            ><Icon name={block.icon?.name} size={block.icon?.size || "x-small"} /></IconButton>

          </Flex.Item>
        )}
        <Flex.Item shouldGrow shouldShrink>
          <Flex direction="column" gap="x-small">
            {block.title && (
              <Text as="div" weight="bold" size="medium" color={config.textColor}
                themeOverride={{
                  warningColor: '#EE6D15'
                }}>
                {block.title}
              </Text>
            )}
            {block?.elements && (
              <View as="div">
                <BlockRenderer blocks={block?.elements} />
              </View>
            )}
          </Flex>
        </Flex.Item>
      </Flex>
    </View>
  );
};

export default CalloutBlock;
